import { useState, type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Send, ShieldCheck, ShieldPlus, UserMinus, X } from "lucide-react";
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import type { AccessGroupItem } from "@/features/identity-hub/api/identity-hub.api";

const BULK_KEY = "iam.orgDrawer.membersTab.bulk";

const BAR_ACTION_CLASS =
  "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-foreground transition-colors hover:bg-background/70 disabled:cursor-not-allowed disabled:opacity-40";

export interface OrgMembersBulkBarProps {
  orgName: string;
  selectedCount: number;
  selectableCount: number;
  assignableRoles: AccessGroupItem[];
  /** How many of the selected members hold each role on this org today, keyed by access id. */
  roleHolderCounts: Map<string, number>;
  removableCount: number;
  resendableCount: number;
  isBusy?: boolean;
  /** False while later pages are still unloaded. Select-all would miss them. */
  canSelectAllMembers?: boolean;
  onToggleAll: () => void;
  onSelectAll: () => void;
  onClear: () => void;
  onChangeRole?: (accessId: string) => void;
  onAddRole?: (accessId: string) => void;
  onResendInvite?: () => void;
  onRemoveFromOrg?: () => void;
}

export function OrgMembersBulkBar({
  orgName,
  selectedCount,
  selectableCount,
  assignableRoles,
  roleHolderCounts,
  removableCount,
  resendableCount,
  isBusy,
  canSelectAllMembers = true,
  onToggleAll,
  onSelectAll,
  onClear,
  onChangeRole,
  onAddRole,
  onResendInvite,
  onRemoveFromOrg,
}: OrgMembersBulkBarProps) {
  const { t } = useTranslation();
  const tb = (key: string, defaultValue: string, opts?: Record<string, unknown>) =>
    t(`${BULK_KEY}.${key}`, { defaultValue, ...opts });

  const isAllSelected = canSelectAllMembers && selectableCount > 0 && selectedCount >= selectableCount;
  const hasRoles = assignableRoles.length > 0;
  const selectAllAria = canSelectAllMembers
    ? tb("selectAllAria", "Select all members on this organization")
    : tb("selectAllPendingAria", "Select all is available after every member page has loaded");

  return (
    <div
      role="toolbar"
      aria-label={tb("toolbarAria", "Bulk actions for selected members")}
      data-testid="org-members-bulk-bar"
      className="flex min-h-11 flex-wrap items-center gap-1 rounded-lg border border-primary/30 bg-primary-subtle px-3 py-1.5"
    >
      <Checkbox
        checked={isAllSelected ? true : "indeterminate"}
        onCheckedChange={() => {
          if (!canSelectAllMembers) return;
          onToggleAll();
        }}
        aria-label={selectAllAria}
        disabled={isBusy || !canSelectAllMembers}
      />
      <span className="ml-1.5 text-[13px] font-semibold text-primary-subtle-foreground">
        {tb("selected", "{{count}} selected", { count: selectedCount })}
      </span>
      {!isAllSelected && (
        <button
          type="button"
          onClick={onSelectAll}
          disabled={isBusy || !canSelectAllMembers}
          className="ml-1.5 inline-flex min-h-11 min-w-11 items-center justify-center px-1.5 text-[12px] font-medium text-primary-subtle-foreground underline underline-offset-2 outline-none hover:no-underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-40"
        >
          {canSelectAllMembers
            ? tb("selectAll", "Select all {{count}}", { count: selectableCount })
            : tb("selectAllPending", "Load every page before selecting all")}
        </button>
      )}
      <span className="mx-1.5 h-5 w-px bg-primary/20" aria-hidden />

      {onChangeRole && hasRoles && (
        <ChangeRolePopover
          orgName={orgName}
          selectedCount={selectedCount}
          roles={assignableRoles}
          roleHolderCounts={roleHolderCounts}
          disabled={isBusy}
          onApply={onChangeRole}
        />
      )}

      {onAddRole && hasRoles && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={BAR_ACTION_CLASS} disabled={isBusy}>
              <ShieldPlus className="size-3.5" aria-hidden />
              {tb("addRole", "Add role")}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
            {assignableRoles.map((role) => (
              <DropdownMenuItem key={role.id} onClick={() => onAddRole(role.id)}>
                {role.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {onResendInvite && (
        <BarAction
          Icon={Send}
          disabled={isBusy || resendableCount === 0}
          title={
            resendableCount === 0
              ? tb("resendDisabledTooltip", "None of the selected members has a pending invite.")
              : undefined
          }
          onClick={onResendInvite}
        >
          {resendableCount > 0
            ? tb("resendInviteCount", "Resend invite ({{count}})", { count: resendableCount })
            : tb("resendInvite", "Resend invite")}
        </BarAction>
      )}

      {onRemoveFromOrg && (
        <BarAction
          Icon={UserMinus}
          tone="danger"
          disabled={isBusy || removableCount === 0}
          title={
            removableCount === 0
              ? tb(
                  "removeDisabledTooltip",
                  "None of the selected members can be removed from this organization (fixed role or only access).",
                )
              : undefined
          }
          onClick={onRemoveFromOrg}
        >
          {tb("removeFromOrg", "Remove from org")}
        </BarAction>
      )}

      <button
        type="button"
        onClick={onClear}
        disabled={isBusy}
        className="ml-auto inline-flex h-8 items-center gap-1 rounded-md px-2 text-[12px] font-medium text-primary-subtle-foreground transition-colors hover:bg-background/70"
      >
        {tb("clear", "Clear")}
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

function BarAction({
  Icon,
  tone = "default",
  disabled,
  title,
  onClick,
  children,
}: {
  Icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  tone?: "default" | "danger";
  disabled?: boolean;
  title?: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(BAR_ACTION_CLASS, tone === "danger" && "text-destructive hover:bg-destructive/10")}
    >
      <Icon className="size-3.5" aria-hidden />
      {children}
    </button>
  );
}

function ChangeRolePopover({
  orgName,
  selectedCount,
  roles,
  roleHolderCounts,
  disabled,
  onApply,
}: {
  orgName: string;
  selectedCount: number;
  roles: AccessGroupItem[];
  roleHolderCounts: Map<string, number>;
  disabled?: boolean;
  onApply: (accessId: string) => void;
}) {
  const { t } = useTranslation();
  const tb = (key: string, defaultValue: string, opts?: Record<string, unknown>) =>
    t(`${BULK_KEY}.${key}`, { defaultValue, ...opts });
  const [isOpen, setIsOpen] = useState(false);
  const [pickedId, setPickedId] = useState<string | null>(null);

  const handleOpenChange = (next: boolean) => {
    setIsOpen(next);
    if (!next) setPickedId(null);
  };

  const handleApply = () => {
    if (!pickedId) return;
    onApply(pickedId);
    handleOpenChange(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(BAR_ACTION_CLASS, "border border-border bg-background hover:bg-muted")}
        >
          <ShieldCheck className="size-3.5" aria-hidden />
          {tb("changeRole", "Change role")}
          <ChevronDown className="size-3 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0">
        <fieldset className="flex flex-col px-1.5 pb-1.5 pt-3">
          <legend className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-[0.4px] text-muted-foreground">
            {tb("setRoleFor", "Set role for {{count}} people", { count: selectedCount })}
          </legend>
          {roles.map((role) => {
            const heldCount = roleHolderCounts.get(role.id) ?? 0;
            const isPicked = pickedId === role.id;
            return (
              <label
                key={role.id}
                className={cn(
                  "flex h-8 cursor-pointer items-center gap-2.5 rounded-md px-2 text-[13px] text-foreground transition-colors",
                  isPicked ? "bg-primary-subtle" : "hover:bg-muted",
                )}
              >
                <input
                  type="radio"
                  name="org-members-bulk-role"
                  value={role.id}
                  checked={isPicked}
                  onChange={() => setPickedId(role.id)}
                  className="peer sr-only"
                />
                <span
                  aria-hidden
                  className={cn(
                    "flex size-3.5 shrink-0 items-center justify-center rounded-full border peer-focus-visible:ring-2 peer-focus-visible:ring-ring/40",
                    isPicked ? "border-primary bg-primary" : "border-input-border bg-background",
                  )}
                >
                  {isPicked && <span className="size-1.5 rounded-full bg-primary-foreground" />}
                </span>
                <span className="min-w-0 flex-1 truncate">{role.name}</span>
                {heldCount > 0 && (
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {tb("heldToday", "{{held}} of {{count}} today", { held: heldCount, count: selectedCount })}
                  </span>
                )}
              </label>
            );
          })}
        </fieldset>
        <div className="flex items-center gap-2 border-t border-border px-3 py-2.5">
          <span className="min-w-0 flex-1 text-[11px] text-muted-foreground">
            {tb("replacesNote", "Replaces their roles in {{orgName}} only", { orgName })}
          </span>
          <Button size="sm" disabled={!pickedId} onClick={handleApply}>
            {tb("applyTo", "Apply to {{count}}", { count: selectedCount })}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
